'use client';

import { type MouseEvent, useEffect, useRef, useState } from 'react';
import './WebVisualizer.css';
import type { ProductInformation, Swatch } from '@dealerportal/common';
import { logger } from '@dealerportal/core';
import { useClientContext, useCtaColor } from '@dealerportal/react-helpers';
import classNames from 'classnames';
import uniqBy from 'lodash/uniqBy';
import Link from 'next/link';
import { BetterButton } from '../../../../../ui-components/src/lib/BetterButton';
import { Samples } from '../ProductCard/Samples';
import { Tooltip } from './Tooltip';
import {
  hasVizLoaderInstance,
  installVizLoaderBroker,
  setVizLoaderInstance,
} from './vizLoaderBroker';

export interface WebVisualizerProps {
  DealerAPIKey: string;
  upid: string;
  productInfo: ProductInformation;
  isMobileDevice: boolean;
  config: {
    accent?: string;
    aspect?: number;
    catalog?: boolean;
    download?: boolean;
    favorite?: boolean;
    languages?: string[];
    lead?: boolean;
    limitroom: boolean;
    logo?: string;
    mode?: string;
    ordersample?: boolean;
    ordersamplecta?: boolean;
    privacy?: boolean;
    product: string;
    productlink?: number;
    requestinfo?: boolean;
    sampletext?: string;
    share?: boolean;
    shareupload?: boolean;
    showwelcome?: number;
    startmenu?: number;
    type?: string;
  };
  standaloneMode?: boolean;
}

export const WebVisualizer = (props: WebVisualizerProps) => {
  const [fullscreen, setFullscreen] = useState(false);
  const [showSampleButton, setShowSampleButton] = useState(false);
  const [showButtonLoader, setShowButtonLoader] = useState(false);
  const [showTooltipIndex, setShowTooltipIndex] = useState<number | null>(null);
  const [currentProduct, setCurrentProduct] = useState<ProductInformation>(
    props.productInfo
  );
  const { disableOrderSample } = useClientContext();
  let currentProductImage: string | null =
    currentProduct.swatches.find((s) => s.UPID === currentProduct.UPID)
      ?.imageURL ?? null;
  currentProductImage = currentProductImage?.includes('res.cloudinary.com')
    ? currentProductImage.replace('https://images.floorforce.com/Textures/', '')
    : currentProductImage;
  const { ctaColor, hoverColor } = useCtaColor();

  const modal = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setFullscreen(false);
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, []);

  useEffect(() => {
    if (props.isMobileDevice) {
      setShowSampleButton(true);
    }
  }, [props.isMobileDevice]);

  const toggleFullscreen = () => {
    setFullscreen(!fullscreen);
    if (hasVizLoaderInstance()) {
      window.vizLoader.sendEvent('forceResize');
    }
  };

  const handleOutsideClick = (event: MouseEvent) => {
    if (fullscreen && event.target === modal.current) {
      setFullscreen(false);
      if (hasVizLoaderInstance()) {
        window.vizLoader.sendEvent('forceResize');
      }
    }
  };

  const updateCurrentProduct = (swatch: Swatch) => {
    const selectedProduct = {
      ...props.productInfo,
      colorName: swatch.colorName,
      UPID: swatch.UPID ?? '',
    };
    setCurrentProduct(selectedProduct);
  };

  const handleClick = (swatch: Swatch) => {
    if (!hasVizLoaderInstance()) return;

    window.vizLoader.sendEvent('setProduct', swatch.UPID);
    updateCurrentProduct(swatch);

    if (currentProduct.UPID !== swatch.UPID) {
      setShowButtonLoader(true);

      setTimeout(() => {
        setShowButtonLoader(false);
      }, 500);
    }
  };

  const fullscreenClassName = classNames({
    'ui-fixed ui-inset-0 ui-z-[999999999999] ui-p-4 ui-pt-6 md:ui-p-8 ui-bg-black/80':
      fullscreen,
  });

  const visIDClassName = classNames(
    'ui-flex ui-aspect-square md:ui-aspect-video ui-w-full ui-h-full',
    {
      'md:ui-max-h-[calc(100vh-7rem)] md:ui-mx-auto ui-overflow-hidden':
        fullscreen,
    }
  );

  const visualizerContainerClassName = classNames('ui-relative ui-bg-white ', {
    'ui-rounded-md ui-border-solid ui-border ui-border-[#EFEFEF]':
      props.standaloneMode,
  });

  const uniqueSwatches: Swatch[] = uniqBy(props.productInfo.swatches, 'UPID');

  const swatchesWithSamples = disableOrderSample
    ? []
    : uniqueSwatches.filter(({ hasSample }) => hasSample);

  useEffect(() => {
    const VISUALIZER_SCRIPT_SRC =
      'https://viz.broadlu.me/dist/visualiserLoader_v2.9.js';

    // Ensure the stable broker facade exists before the loader script resolves,
    // so an analytics subscriber polling for window.vizLoader finds it right
    // away rather than timing out on a page that mounted the visualizer late.
    installVizLoaderBroker();

    const initLoader = () => {
      logger.info('Visualizer loaded successfully');
      const loader = new window['visualiserLoader'](
        'visID',
        props.DealerAPIKey,
        props?.standaloneMode
          ? {
              desktopHeightPercent: 70,
              desktopWidthPercent: 100,
              mobileHeightPercent: 80,
            }
          : {},
        props.config,
        false
      );
      // Hand the fresh instance to the broker instead of overwriting
      // window.vizLoader. Each client-side navigation mounts a new loader; the
      // broker replays every recorded subscription onto it, so analytics stays
      // wired up without re-running the subscriber's one-shot install().
      setVizLoaderInstance(loader);

      loader.addListener('currentLanguage', (lang: string) => {
        if (!lang || !window?.['doGTranslate']) return;

        const select = document.querySelector(
          '.gtranslate_wrapper select'
        ) as HTMLSelectElement;

        if (!select || !select?.value) return;

        select.value = `en|${lang}`;
        select.dispatchEvent(new Event('change'));
      });

      loader.addListener('loaded', (_data: unknown) => {
        const iframe = document.querySelector(
          '#visID iframe'
        ) as HTMLIFrameElement;
        if (iframe) {
          iframe.className = 'ui-w-full ui-h-full';
        }
        loader.sendEvent('forceResize');
      });
    };

    // The loader library defines window.visualiserLoader once and its <script>
    // tag persists across client-side navigations. Re-injecting it would add a
    // duplicate tag on every PDP visit, so construct directly when it already
    // exists and only inject the script on the first load.
    if (typeof window['visualiserLoader'] === 'function') {
      initLoader();
      return;
    }

    const script = document.createElement('script');
    script.src = VISUALIZER_SCRIPT_SRC;
    script.async = true;
    script.onload = initLoader;
    document.body.appendChild(script);
  }, []);

  return (
    <>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
      <div
        ref={modal}
        onClick={handleOutsideClick}
        className={fullscreenClassName}
        role="presentation"
      >
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          className={visualizerContainerClassName}
          onMouseEnter={() =>
            !props.isMobileDevice && setShowSampleButton(true)
          }
          onMouseLeave={() =>
            !props.isMobileDevice && setShowSampleButton(false)
          }
        >
          <div id="visID" className={visIDClassName} />
          {!props?.standaloneMode && (
            <div className="ui-w-full ui-mx-auto ui-px-1 md:ui-px-4 ui-py-1 ui-bg-[#EFEFEF] ui-border-solid ui-border-0 ui-border-t ui-border-gray-300 ui-rounded-b-md">
              <div className="ui-flex ui-items-start ui-justify-between ui-gap-12">
                <ul className="__uniqueSwatches ui-flex ui-flex-wrap ui-justify-start ui-gap-0.5 !ui-m-0 ui-p-0 !ui-list-none">
                  {uniqueSwatches
                    .filter(({ hasVisualization }) => hasVisualization)
                    .map((swatch, index) => (
                      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                      // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
                      <li
                        onClick={() => handleClick(swatch)}
                        onMouseLeave={() => setShowTooltipIndex(null)}
                        onMouseEnter={() => setShowTooltipIndex(index)}
                        key={swatch.UPID}
                        title={swatch.colorName}
                        className="ui-cursor-pointer ui-relative ui-h-9 ui-w-9 ui-leading-none"
                      >
                        <Tooltip
                          tip={swatch.colorName}
                          show={index === showTooltipIndex}
                          position="top-center"
                        />
                        <img
                          className={classNames(
                            'ui-p-0.5 ui-shrink-0 ui-border-solid ui-border-2 ui-border-transparent ui-rounded-md',
                            {
                              '!ui-border-[var(--cta-color,#030712)]':
                                swatch.UPID === currentProduct.UPID,
                            }
                          )}
                          src={
                            swatch.imageURL.includes('res.cloudinary.com')
                              ? swatch.imageURL.replace(
                                  'https://images.floorforce.com/Textures/',
                                  ''
                                )
                              : swatch.imageURL
                          }
                          alt={swatch.colorName}
                          height={36}
                          width={36}
                        />
                      </li>
                    ))}
                </ul>
                {!props.isMobileDevice && (
                  <BetterButton
                    btnStyle={fullscreen ? 'neutral' : 'simple'}
                    icon={
                      !fullscreen
                        ? `<svg
                class="ui-w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
              >
                <path
                  class="ui-fill-gray-600"
                  d="M32 32C14.3 32 0 46.3 0 64v96c0 17.7 14.3 32 32 32s32-14.3 32-32V96h64c17.7 0 32-14.3 32-32s-14.3-32-32-32H32zM64 352c0-17.7-14.3-32-32-32s-32 14.3-32 32v96c0 17.7 14.3 32 32 32h96c17.7 0 32-14.3 32-32s-14.3-32-32-32H64V352zM320 32c-17.7 0-32 14.3-32 32s14.3 32 32 32h64v64c0 17.7 14.3 32 32 32s32-14.3 32-32V64c0-17.7-14.3-32-32-32H320zM448 352c0-17.7-14.3-32-32-32s-32 14.3-32 32v64H320c-17.7 0-32 14.3-32 32s14.3 32 32 32h96c17.7 0 32-14.3 32-32V352z"
                />
              </svg>`
                        : ''
                    }
                    iconSize="4"
                    label={fullscreen ? 'ESC' : ''}
                    className="ui-h-8 ui-font-semibold"
                    replaceClass={{
                      '!ui-px-6': '!ui-px-3',
                      '!ui-text-sm': fullscreen
                        ? '!ui-text-[11px]'
                        : '!ui-text-sm',
                    }}
                    labelClassName={
                      fullscreen
                        ? '!ui-text-[11px] ui-leading-none'
                        : '!ui-text-sm'
                    }
                    attributes={{
                      type: 'button',
                      onClick: () => toggleFullscreen(),
                    }}
                  />
                )}
              </div>

              {swatchesWithSamples.length > 0 && (
                <div
                  className={classNames(
                    {
                      'ui-hidden': !showSampleButton,
                      'ui-block': showSampleButton,
                    },
                    'web-visualizer__samples md:ui-absolute md:ui-top-4 md:ui-left-4 md:ui-bg-white/80 md:ui-rounded'
                  )}
                >
                  <div className="ui-p-1 ui-pb-1 md:ui-p-4">
                    <div className="ui-flex ui-gap-3 ui-mt-4 md:ui-mt-0 ui-mb-3">
                      {currentProductImage && (
                        <img
                          style={{
                            height: '72px',
                            width: '72px',
                          }}
                          className="ui-hidden md:ui-block ui-rounded-md"
                          src={currentProductImage as string}
                          alt={currentProduct.colorName}
                          height="72"
                          width="72"
                        />
                      )}
                      <div className="ui-flex-1 ui-pr-4 ui-font-semibold">
                        <div className="ui-mb-1">
                          <div className="ui-text-gray-950 ui-text-base">
                            {currentProduct.brandName}
                          </div>
                          <div className="ui-text-gray-600 ui-text-sm">
                            {currentProduct.productName}
                          </div>
                        </div>
                        <div className="ui-text-gray-600 ui-text-sm">
                          {currentProduct.colorName}
                        </div>
                      </div>
                    </div>
                    {swatchesWithSamples.map(
                      (swatch) =>
                        props.productInfo.hasSample && (
                          <Samples
                            key={swatch.UPID}
                            samplePrice={props.productInfo.samplePrice}
                            productInfo={swatch}
                            visible={currentProduct.UPID === swatch.UPID}
                            isLoading={showButtonLoader}
                            replaceClass={{
                              '!ui-w-max': 'ui-w-max md:ui-w-full',
                            }}
                          />
                        )
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <VisualizerCta
            showVisualizerCta={props.standaloneMode}
            ctaColor={ctaColor}
            hoverColor={hoverColor}
          />
        </div>
      </div>
    </>
  );
};

const VisualizerCta = ({
  showVisualizerCta = false,
  ctaColor,
  hoverColor,
}: {
  showVisualizerCta?: boolean;
  ctaColor?: string;
  hoverColor?: string;
}) => {
  if (!showVisualizerCta) return null;
  return (
    <div className="ui-flex ui-justify-center ui-flex-col md:ui-flex-row ui-mt-5 ui-mb-12 ui-mx-8 ui-items-center ui-gap-2">
      <div className="ui-mx-6 ui-my-2 ui-text-black-500 ui-text-md ui-font-bold">
        Not finding what you are looking for?
      </div>
      <Link href="/p" prefetch={false}>
        <button
          style={{
            ['--bg-color' as string]: ctaColor,
            ['--hover-bg' as string]: hoverColor,
            textTransform: 'none',
          }}
          className={`ui-border-none !ui-normal-case
                      ui-text-white ui-px-4 ui-py-3 ui-rounded-full ui-bg-[--bg-color]
                      hover:ui-bg-[--hover-bg] ui-transition-all ui-duration-200 ui-text-base
                      ui-max-w-md ui-cursor-pointer`}
        >
          <span className="ui-normal-case">
            Browse our complete flooring catalog
          </span>
        </button>
      </Link>
    </div>
  );
};
